// Shared context for the boat-scoped app chrome. BoatShell provides it; the
// dashboard / devices / manuals / detail pages and device rows consume it so a
// row buried in a section can open a manual or the search overlay without prop
// drilling.

import { createContext, useContext } from 'react';
import type { OverviewArchetype, BoatNamesSnapshot } from '@neomaris/protocol';

import type { BoatPresence } from '../../lib/boatPresence';
import type { BoatStateResponse } from '../../lib/useBoatInfo';
import type { NeoStatusState } from '../../lib/useNeoStatus';
import type { Boat } from '../../lib/useBoats';
import type { UseBoatAlerts } from '../../lib/useBoatAlerts';
import type { UseBoatDevices, VesselDevice } from '../../lib/useBoatDevices';
import type {
  AnchorCommandStatusEvent,
  BoardChangedEvent,
  ConnectionStatus,
  LiveSample,
  LooksChangedEvent,
} from '../../lib/useBoatLiveData';
import type { LiveSampleStore } from '../../lib/liveSampleStore';
import type { NeoBoardSummary } from '../../lib/neoBoards';
import type { NeoBoardsControls } from '../../lib/useNeoBoards';

export interface BoatChrome {
  boatId: string;
  /** Resolved boat (name + presence), or null while loading / not found. */
  boat: Boat | null;
  /** The shared device inventory hook result (one WS subscription per boat). */
  devices: UseBoatDevices;
  /** The shared alarm-state hook result (one WS subscription per boat). */
  alerts: UseBoatAlerts;
  /** Latest boat-level live telemetry, as a snapshot map. Republished at most
   *  once a second — every consumer of this re-renders when it changes, which
   *  is the whole page. For a value that must move smoothly, read `liveStore`
   *  instead. */
  liveSamples: ReadonlyMap<string, LiveSample> | undefined;
  /** The per-path live lane: subscribe to one path, wake only on that path.
   *  Stable identity, safe to put in a dependency array. */
  liveStore: LiveSampleStore;
  /** Epoch-ms of the most recent live WS sample for this boat (presence). */
  lastSampleAtMs: number | undefined;
  /** The live socket's own state. Published all along and rendered nowhere,
   *  which is why a boat leaving cell coverage used to freeze every value on
   *  screen with nothing saying so. */
  liveStatus: ConnectionStatus;
  /** Changes whenever the shared user WebSocket invalidates the boat-information aggregate. */
  infoRevision: number;
  /** Latest anchor-command status beat for this boat (cmdAck lane, ADR 0135). */
  anchorCommandStatus: AnchorCommandStatusEvent | undefined;
  /** The newest `board_changed` frame for this boat, from any device (TOM-192). */
  boardChanged: BoardChangedEvent | undefined;
  /**
   * Changes whenever an owner, on any device, answers about the boat's
   * equipment (`equipment_changed`, cloud ADR 0312 §7, TOM-409).
   */
  equipmentRevision: number;
  namesRevision?: number;
  /**
   * The newest `looks` frame for this boat: the signed-in owner's own panel
   * looks changed, on any of their screens (TOM-595). Undefined until one
   * arrives.
   */
  looksChanged?: LooksChangedEvent;
  names?: BoatNamesSnapshot | null;
  /** The boat's boards, as the sidebar, the bar and Your boards list them (TOM-192). */
  boards: NeoBoardsControls;
  /**
   * The system board for the boat's mode, which takes the bar's mode slot
   * (TOM-358, ADR 0306 §3); null while the mode is unknown or the board is not
   * listed.
   */
  modeBoard: NeoBoardSummary | null;
  /**
   * The boat's mode as the cloud folds it (OverviewArchetype), null when the
   * boat can't tell, undefined until the boat's information is read.
   */
  archetype: OverviewArchetype | null | undefined;
  /** Open a device's manual: a single manual opens directly, several open the sheet. */
  openManual: (device: VesselDevice) => void;
  /** Read the boat list again, after a change only it carries (the boat's photo, TOM-683). */
  refreshBoats?: () => void;
  /** Open the blended search overlay, optionally seeded with a query. */
  openSearch: (initialQuery?: string) => void;
}

/**
 * The shell's header parts the boat's home draws itself (TOM-510, cloud ADR
 * 0335): home has its own fixed header in the shell header's place, so the
 * shell hands it the presence, Neo's status (whose headline every header
 * shows, TOM-526), the menu, the mode selector and the boat's mode. Its own
 * context, because the presence moves every second and only home reads it.
 */
export interface HomeChrome {
  presence: BoatPresence;
  presenceLabel: string;
  /** Neo's status for the boat, which the shell reads once for every header (TOM-526). */
  status: NeoStatusState;
  menuOpen: boolean;
  openMenu: () => void;
  /** The button the mode selector is open from, or null when it is closed. */
  modeOpenFrom: HTMLElement | null;
  /** Open the mode selector under `button`; it gives the focus back to it on close (TOM-526). */
  openMode: (button: HTMLElement, align?: 'start' | 'end') => void;
  /** The boat's own answer about its mode, null until it is read or when it never reported. */
  boatState: BoatStateResponse | null;
  anchorWatchOn: boolean;
  /**
   * Wider than 768 px Neo's chat slides in over the page from the shell
   * (TOM-738), opened by its sparkle (TOM-808): home keeps no chat of its own.
   */
  slideChat: boolean;
  /**
   * Home's chat docked over the board, wider than 768 px (TOM-920): whether it
   * is open, as the owner left it, and the way to open or close it.
   */
  homeChat: { docked: boolean; open: boolean; setOpen: (open: boolean) => void };
}

export const BoatChromeContext = createContext<BoatChrome | null>(null);

export const HomeChromeContext = createContext<HomeChrome | null>(null);

export function useHomeChrome(): HomeChrome {
  const ctx = useContext(HomeChromeContext);
  if (ctx === null) {
    throw new Error('useHomeChrome must be used inside <BoatShell>');
  }
  return ctx;
}

export function useBoatChrome(): BoatChrome {
  const ctx = useContext(BoatChromeContext);
  if (ctx === null) {
    throw new Error('useBoatChrome must be used inside <BoatShell>');
  }
  return ctx;
}

/** Map a freshness state to a presence dot modifier (green / amber / red). */
export function presenceDotClass(state: 'fresh' | 'stale' | 'cold' | 'never'): string {
  if (state === 'fresh') return 'online';
  if (state === 'stale') return 'connecting';
  return 'offline';
}
